// @ts-ignore TODO: remove once types are added
import getFormattedSwapData from '@kwenta/synthswap';
import { wei } from '@kwenta/wei';
import axios from 'axios';
import { get, keyBy } from 'lodash';
import { formatEther, formatUnits, maxUint256, parseEther, toHex } from 'viem';
import * as sdkErrors from '../constants/errors';
import { ADDITIONAL_MARKETS, ATOMIC_EXCHANGES_L1, ATOMIC_EXCHANGE_SLIPPAGE, CG_BASE_API_URL, CRYPTO_CURRENCY_MAP, DEFAULT_1INCH_SLIPPAGE, ETH_ADDRESS, ETH_COINGECKO_ADDRESS, KWENTA_ONE_INCH_REFERRAL_ADDRESS, SYNTH_SWAP_OPTIMISM_ADDRESS, PROTOCOLS, FILTERED_TOKENS, } from '../constants/exchange';
import { KWENTA_TRACKING_CODE } from '../constants/futures';
import { UNIT_BIG_INT } from '../constants/number';
import erc20Abi from '../contracts/abis/ERC20';
import { ADDRESSES } from '../contracts/constants';
import { getSynthsForNetwork } from '../data/synths';
import { weiFromWei } from '../utils';
import { synthToAsset } from '../utils/exchange';
import { getReasonFromCode } from '../utils/synths';
export default class ExchangeService {
    constructor(sdk) {
        this.tokensMap = {};
        this.tokenList = [];
        this.sdk = sdk;
    }
    get exchangeRates() {
        return this.sdk.prices.currentPrices.onChain;
    }
    get usdcDecimals() {
        return this.sdk.context.networkId === 8453 || this.sdk.context.networkId === 84532 ? 6 : 18;
    }
    /**
     * @desc - Get the provider to be used for transactions on a currency pair.
     * @param fromCurrencyKey The currency key of the source token.
     * @param toCurrencyKey The currency key of the destination token.
     * @returns Returns one of '1inch', 'synthetix', or 'synthswap'.
     */
    getTxProvider(fromCurrencyKey, toCurrencyKey) {
        if (!toCurrencyKey || !fromCurrencyKey)
            return undefined;
        if (this.synthsMap?.[toCurrencyKey] &&
            this.synthsMap?.[fromCurrencyKey])
            return 'synthetix';
        if (this.tokensMap[toCurrencyKey] && this.tokensMap[fromCurrencyKey])
            return '1inch';
        return 'synthswap';
    }
    async getTradePrices(txProvider, fromCurrencyKey, toCurrencyKey, fromAmount, toAmount) {
        const coinGeckoPrices = await this.getCoingeckoPrices(fromCurrencyKey, toCurrencyKey);
        const [quotePriceRate, basePriceRate] = await Promise.all([fromCurrencyKey, toCurrencyKey].map((currencyKey) => this.getPriceRate(currencyKey, txProvider, coinGeckoPrices)));
        let quoteTradePrice = fromAmount.mul(quotePriceRate || 0);
        let baseTradePrice = toAmount.mul(basePriceRate || 0);
        if (this.sUSDRate) {
            quoteTradePrice = quoteTradePrice.div(this.sUSDRate);
            baseTradePrice = baseTradePrice.div(this.sUSDRate);
        }
        return { quoteTradePrice, baseTradePrice };
    }
    async getSlippagePercent(fromCurrencyKey, toCurrencyKey, fromAmount, toAmount) {
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        if (txProvider === '1inch') {
            const { quoteTradePrice: totalTradePrice, baseTradePrice: estimatedBaseTradePrice } = await this.getTradePrices(txProvider, fromCurrencyKey, toCurrencyKey, fromAmount, toAmount);
            if (totalTradePrice.gt(0) && estimatedBaseTradePrice.gt(0)) {
                return totalTradePrice.sub(estimatedBaseTradePrice).div(totalTradePrice).neg();
            }
        }
        return undefined;
    }
    async getBaseFeeRate(fromCurrencyKey, toCurrencyKey) {
        const { SystemSettings } = this.sdk.context.defaultContractConfigs;
        if (!SystemSettings)
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        const [sourceCurrencyFeeRate, destinationCurrencyFeeRate] = await this.sdk.context.client.multicall({
            contracts: [
                {
                    ...SystemSettings,
                    functionName: 'exchangeFeeRate',
                    args: [toHex(toCurrencyKey)],
                },
                {
                    ...SystemSettings,
                    functionName: 'exchangeFeeRate',
                    args: [toHex(fromCurrencyKey)],
                },
            ],
        });
        return sourceCurrencyFeeRate.result && destinationCurrencyFeeRate.result
            ? wei(sourceCurrencyFeeRate.result.toString() + destinationCurrencyFeeRate.result.toString())
            : wei(0);
    }
    /**
     * @desc - Get the fee rate for exchanging between two currencies.
     * @param fromCurrencyKey The currency key of the source token.
     * @param toCurrencyKey The currency key of the destination token.
     * @returns Returns the fee rate.
     */
    async getExchangeFeeRate(fromCurrencyKey, toCurrencyKey) {
        const { Exchanger } = this.sdk.context.defaultContractConfigs;
        if (!Exchanger) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const exchangeFeeRate = await this.sdk.context.client.readContract({
            ...Exchanger,
            functionName: 'feeRateForExchange',
            args: [toHex(fromCurrencyKey), toHex(toCurrencyKey)],
        });
        return wei(exchangeFeeRate.toString());
    }
    async getRate(fromCurrencyKey, toCurrencyKey) {
        const fromTokenAddress = this.getTokenAddress(fromCurrencyKey);
        const toTokenAddress = this.getTokenAddress(toCurrencyKey);
        const [[quoteRate, baseRate], coinGeckoPrices] = await Promise.all([
            this.getPairRates(fromCurrencyKey, toCurrencyKey),
            this.getCoingeckoPrices(fromCurrencyKey, toCurrencyKey),
        ]);
        const base = baseRate.lte(0)
            ? this.getCoingeckoPricesForCurrencies(coinGeckoPrices, toTokenAddress)
            : baseRate;
        const quote = quoteRate.lte(0)
            ? this.getCoingeckoPricesForCurrencies(coinGeckoPrices, fromTokenAddress)
            : quoteRate;
        return base.gt(0) && quote.gt(0) ? quote.div(base) : wei(0);
    }
    /**
     * @desc Get the list of whitelisted tokens on 1inch.
     * @returns Returns the list of tokens currently whitelisted on 1inch.
     */
    async getOneInchTokenList() {
        const response = await axios.get(this.oneInchApiUrl + 'tokens');
        const tokensMap = response.data.tokens || {};
        const chainId = this.sdk.context.isL2 ? 10 : 1;
        const tokens = Object.values(tokensMap).map((t) => ({ ...t, chainId, tags: [] }));
        return {
            tokens,
            tokensMap: keyBy(tokens, 'symbol'),
            symbols: tokens.map((token) => token.symbol),
        };
    }
    async getFeeReclaimPeriod(walletAddress, currencyKey) {
        const { Exchanger } = this.sdk.context.defaultContractConfigs;
        if (!Exchanger) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const maxSecsLeftInWaitingPeriod = await this.sdk.context.client.readContract({
            ...Exchanger,
            functionName: 'maxSecsLeftInWaitingPeriod',
            args: [walletAddress, toHex(currencyKey)],
        });
        return Number(maxSecsLeftInWaitingPeriod);
    }
    async swapSynthSwap(fromToken, toToken, fromAmount) {
        const { Exchanger, SynthSwap } = this.sdk.context.defaultContractConfigs;
        if (!Exchanger) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const sUSD = this.tokensMap['sUSD'];
        const oneInchFrom = this.tokensMap[fromToken.symbol] ? sUSD.address : fromToken.address;
        const oneInchTo = this.tokensMap[toToken.symbol] ? sUSD.address : toToken.address;
        const fromSymbolBytes = toHex(fromToken.symbol);
        const sUSDBytes = toHex('sUSD');
        let synthAmountEth = fromAmount;
        if (this.tokensMap[fromToken.symbol]) {
            const fromAmountWei = wei(fromAmount).toString(0, true);
            const [amountReceived, fee] = await this.sdk.context.client.readContract({
                ...Exchanger,
                functionName: 'getAmountsForExchange',
                args: [BigInt(fromAmountWei), fromSymbolBytes, sUSDBytes],
            });
            const usdValue = amountReceived - fee;
            synthAmountEth = formatEther(usdValue);
        }
        const params = await this.getOneInchSwapParams(oneInchFrom, oneInchTo, synthAmountEth, fromToken.decimals);
        const formattedData = getFormattedSwapData(params, SYNTH_SWAP_OPTIMISM_ADDRESS);
        if (!SynthSwap) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        if (this.tokensMap[toToken.symbol]) {
            const symbolBytes = toHex(toToken.symbol);
            if (formattedData.functionSelector === 'swap') {
                const { request } = await this.sdk.context.client.simulateContract({
                    ...SynthSwap,
                    args: [symbolBytes, formattedData.data],
                    functionName: 'swapInto',
                });
                return this.sdk.transactions.writeContract(request);
            }
            else {
                const { request } = await this.sdk.context.client.simulateContract({
                    ...SynthSwap,
                    args: [
                        symbolBytes,
                        fromToken.address,
                        BigInt(synthAmountEth),
                        formattedData.data,
                    ],
                    functionName: 'uniswapSwapInto',
                });
                return this.sdk.transactions.writeContract(request);
            }
        }
        else {
            if (formattedData.functionSelector === 'swap') {
                const { request } = await this.sdk.context.client.simulateContract({
                    ...SynthSwap,
                    args: [fromSymbolBytes, BigInt(wei(fromAmount).toString(0, true)), formattedData.data],
                    functionName: 'swapOutOf',
                });
                return this.sdk.transactions.writeContract(request);
            }
            else {
                const usdValue = BigInt(parseEther(synthAmountEth));
                const { request } = await this.sdk.context.client.simulateContract({
                    ...SynthSwap,
                    args: [
                        fromSymbolBytes,
                        toToken.address,
                        BigInt(wei(fromAmount).toString(0, true)),
                        usdValue,
                        formattedData.data,
                    ],
                    functionName: 'uniswapSwapOutOf',
                });
                return this.sdk.transactions.writeContract(request);
            }
        }
    }
    async swapOneInch(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals) {
        const params = await this.getOneInchSwapParams(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals);
        const { from, to, data, value } = params.tx;
        return this.sdk.transactions.sendTransaction({
            account: from,
            to: to,
            data: data,
            value: BigInt(value),
            chain: this.sdk.context.client.chain,
        });
    }
    async swapOneInchGasEstimate(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals) {
        const params = await this.getOneInchSwapParams(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals);
        return params.tx.gas;
    }
    async getNumEntries(currencyKey) {
        const { Exchanger } = this.sdk.context.defaultContractConfigs;
        if (!Exchanger)
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        const [_reclaimAmount, _rebateAmount, numEntries] = await this.sdk.context.client.readContract({
            ...Exchanger,
            functionName: 'settlementOwing',
            args: [this.sdk.context.walletAddress, toHex(currencyKey)],
        });
        return numEntries ? Number(numEntries) : 0;
    }
    async getAtomicRates(currencyKey) {
        const { ExchangeRates } = this.sdk.context.defaultContractConfigs;
        if (!ExchangeRates)
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        const [value] = await this.sdk.context.client.readContract({
            ...ExchangeRates,
            functionName: 'effectiveAtomicValueAndRates',
            args: [toHex(currencyKey), UNIT_BIG_INT, toHex('sUSD')],
        });
        return weiFromWei(value.toString()) ?? wei(0);
    }
    async approveSwap(fromCurrencyKey, toCurrencyKey) {
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        const fromCurrencyContract = this.getCurrencyContract(fromCurrencyKey);
        const approveAddress = await this.getApproveAddress(txProvider);
        if (fromCurrencyContract) {
            const { request } = await this.sdk.context.client.simulateContract({
                ...fromCurrencyContract,
                functionName: 'approve',
                args: [approveAddress, maxUint256],
            });
            return this.sdk.transactions.writeContract(request);
        }
        return undefined;
    }
    async handleSettle(toCurrencyKey) {
        const { Exchanger } = this.sdk.context.defaultContractConfigs;
        if (!Exchanger) {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
        const numEntries = await this.getNumEntries(toCurrencyKey);
        if (numEntries > 12) {
            const destinationCurrencyKey = toHex(toCurrencyKey);
            const { request } = await this.sdk.context.client.simulateContract({
                ...Exchanger,
                functionName: 'settle',
                args: [this.sdk.context.walletAddress, destinationCurrencyKey],
            });
            return this.sdk.transactions.writeContract(request);
        }
        return undefined;
    }
    // TODO: Refactor handleExchange
    async handleExchange(fromCurrencyKey, toCurrencyKey, fromAmount, toAmount) {
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        const fromTokenAddress = this.getTokenAddress(fromCurrencyKey);
        const toTokenAddress = this.getTokenAddress(toCurrencyKey);
        const fromTokenDecimals = this.getTokenDecimals(fromCurrencyKey);
        let txHash = null;
        if (txProvider === '1inch' && !!this.tokensMap) {
            txHash = await this.swapOneInch(fromTokenAddress, toTokenAddress, fromAmount, fromTokenDecimals);
        }
        else if (txProvider === 'synthswap') {
            // @ts-ignore TODO: Fix variable types
            txHash = await this.swapSynthSwap(this.allTokensMap[fromCurrencyKey], this.allTokensMap[toCurrencyKey], fromAmount);
        }
        else {
            const isAtomic = this.checkIsAtomic(fromCurrencyKey, toCurrencyKey);
            const { Synthetix } = this.sdk.context.defaultContractConfigs;
            if (!Synthetix)
                throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
            if (isAtomic) {
                const exchangeParams = this.getAtomicExchangeParams(fromCurrencyKey, toCurrencyKey, wei(fromAmount), wei(toAmount).mul(wei(1).sub(ATOMIC_EXCHANGE_SLIPPAGE)));
                const { request } = await this.sdk.context.client.simulateContract({
                    ...Synthetix,
                    functionName: 'exchangeAtomically',
                    args: exchangeParams,
                });
                const hash = await this.sdk.transactions.writeContract(request);
                return hash;
            }
            else {
                const { request } = await this.sdk.context.client.simulateContract({
                    ...Synthetix,
                    functionName: 'exchangeWithTracking',
                    args: this.getExchangeParams(fromCurrencyKey, toCurrencyKey, wei(fromAmount)),
                });
                const hash = await this.sdk.transactions.writeContract(request);
                return hash;
            }
        }
        return txHash;
    }
    async getFeeCost(fromCurrencyKey, toCurrencyKey, fromAmount) {
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        const coinGeckoPrices = await this.getCoingeckoPrices(fromCurrencyKey, toCurrencyKey);
        const [exchangeFeeRate, fromPriceRate] = await Promise.all([
            this.getExchangeFeeRate(fromCurrencyKey, toCurrencyKey),
            this.getPriceRate(fromCurrencyKey, txProvider, coinGeckoPrices),
        ]);
        return wei(fromAmount).mul(exchangeFeeRate).mul(fromPriceRate);
    }
    getApproveAddress(txProvider) {
        return txProvider !== '1inch' ? SYNTH_SWAP_OPTIMISM_ADDRESS : this.getOneInchApproveAddress();
    }
    async checkAllowance(fromCurrencyKey, toCurrencyKey) {
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        const [fromCurrencyContract, approveAddress] = await Promise.all([
            this.getCurrencyContract(fromCurrencyKey),
            this.getApproveAddress(txProvider),
        ]);
        if (!!fromCurrencyContract) {
            const allowance = await this.sdk.context.client.readContract({
                ...fromCurrencyContract,
                functionName: 'allowance',
                args: [this.sdk.context.walletAddress, approveAddress],
            });
            return weiFromWei(allowance.toString());
        }
        else {
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        }
    }
    getCurrencyName(currencyKey) {
        return this.allTokensMap?.[currencyKey]?.name;
    }
    async getOneInchQuote(toCurrencyKey, fromCurrencyKey, amount) {
        const sUSD = this.tokensMap['sUSD'];
        const decimals = this.getTokenDecimals(fromCurrencyKey);
        const fromTokenAddress = this.getTokenAddress(fromCurrencyKey);
        const toTokenAddress = this.getTokenAddress(toCurrencyKey);
        const txProvider = this.getTxProvider(fromCurrencyKey, toCurrencyKey);
        const synth = this.tokensMap[fromCurrencyKey] || this.tokensMap[toCurrencyKey];
        const synthUsdRate = synth ? this.getPairRates(synth, 'sUSD') : null;
        if (!fromCurrencyKey || !toCurrencyKey || !sUSD || !amount.length || wei(amount).eq(0)) {
            return '';
        }
        if (txProvider === '1inch') {
            const estimatedAmount = await this.quoteOneInch(fromTokenAddress, toTokenAddress, amount, decimals);
            return estimatedAmount;
        }
        if (this.tokensMap[fromCurrencyKey]) {
            const usdAmount = wei(amount).div(synthUsdRate);
            const estimatedAmount = await this.quoteOneInch(sUSD.address, toTokenAddress, usdAmount.toString(), decimals);
            return estimatedAmount;
        }
        else {
            const estimatedAmount = await this.quoteOneInch(fromTokenAddress, sUSD.address, amount, decimals);
            return wei(estimatedAmount).mul(synthUsdRate).toString();
        }
    }
    async getPriceRate(currencyKey, txProvider, coinGeckoPrices) {
        const tokenAddress = this.getTokenAddress(currencyKey, true).toLowerCase();
        if (txProvider !== 'synthetix') {
            const sUSDRate = this.exchangeRates['sUSD'];
            const price = coinGeckoPrices && coinGeckoPrices[tokenAddress];
            if (price && sUSDRate?.gt(0)) {
                return wei(price.usd ?? 0).div(sUSDRate);
            }
            else {
                return wei(0);
            }
        }
        else {
            return this.checkIsAtomic('sUSD', currencyKey)
                ? await this.getAtomicRates(currencyKey)
                : this.getExchangeRatesForCurrencies(this.exchangeRates, currencyKey, 'sUSD');
        }
    }
    validCurrencyKeys(fromCurrencyKey, toCurrencyKey) {
        return [fromCurrencyKey, toCurrencyKey].map((currencyKey) => {
            return (!!currencyKey &&
                (!!this.synthsMap[currencyKey] || !!this.tokensMap[currencyKey]));
        });
    }
    async getCoingeckoPrices(fromCurrencyKey, toCurrencyKey) {
        const fromTokenAddress = this.getTokenAddress(fromCurrencyKey, true).toLowerCase();
        const toTokenAddress = this.getTokenAddress(toCurrencyKey).toLowerCase();
        const tokenAddresses = [fromTokenAddress, toTokenAddress];
        return this.batchGetCoingeckoPrices(tokenAddresses);
    }
    async batchGetCoingeckoPrices(tokenAddresses, include24hrChange = false) {
        const platform = this.sdk.context.isL2 || this.sdk.context.isBase ? 'optimistic-ethereum' : 'ethereum';
        const response = await axios.get(`${CG_BASE_API_URL}/simple/token_price/${platform}?contract_addresses=${tokenAddresses
            .join(',')
            .replace(ETH_ADDRESS, ETH_COINGECKO_ADDRESS)}&vs_currencies=usd${include24hrChange ? '&include_24hr_change=true' : ''}`);
        return response.data;
    }
    get sUSDRate() {
        return this.exchangeRates['sUSD'];
    }
    getAtomicExchangeParams(fromCurrencyKey, toCurrencyKey, sourceAmount, minAmount) {
        const sourceCurrencyKey = toHex(fromCurrencyKey);
        const destinationCurrencyKey = toHex(toCurrencyKey);
        const sourceAmountBigInt = sourceAmount.toBigInt();
        return [
            sourceCurrencyKey,
            sourceAmountBigInt,
            destinationCurrencyKey,
            KWENTA_TRACKING_CODE,
            minAmount.toBigInt(),
        ];
    }
    getExchangeParams(fromCurrencyKey, toCurrencyKey, sourceAmount) {
        const sourceCurrencyKey = toHex(fromCurrencyKey);
        const destinationCurrencyKey = toHex(toCurrencyKey);
        const sourceAmountBigInt = sourceAmount.toBigInt();
        return [
            sourceCurrencyKey,
            sourceAmountBigInt,
            destinationCurrencyKey,
            this.sdk.context.walletAddress,
            KWENTA_TRACKING_CODE,
        ];
    }
    getSynthsMap() {
        return this.synthsMap;
    }
    get synthsMap() {
        return getSynthsForNetwork(this.sdk.context.networkId);
    }
    async getOneInchTokens() {
        const { tokensMap, tokens } = await this.getOneInchTokenList();
        this.tokensMap = tokensMap;
        this.tokenList = tokens;
        this.allTokensMap = { ...this.synthsMap, ...tokensMap };
        return { tokensMap: this.tokensMap, tokenList: this.tokenList };
    }
    async getSynthSuspensions() {
        const { SystemStatus } = this.sdk.context.defaultContractConfigs;
        if (!SystemStatus)
            throw new Error(sdkErrors.UNSUPPORTED_NETWORK);
        const synthsMap = this.sdk.exchange.getSynthsMap();
        const calls = Object.keys(synthsMap).map((key) => ({
            ...SystemStatus,
            functionName: 'synthExchangeSuspension',
            args: [toHex(key)],
        }));
        const responses = await this.sdk.context.client.multicall({ contracts: calls });
        let ret = {};
        let i = 0;
        for (let synth in synthsMap) {
            const [isSuspended, reason] = responses[i].result;
            const reasonCode = Number(reason);
            ret[synth] = {
                isSuspended: isSuspended,
                reasonCode,
                reason: isSuspended ? getReasonFromCode(reasonCode) : null,
            };
            i++;
        }
        return ret;
    }
    /**
     * Get token balances for the given wallet address
     * @param walletAddress Wallet address
     * @returns Token balances for the given wallet address
     */
    async getTokenBalances(walletAddress) {
        // TODO: Only needed for goerli testing perposes
        if (this.sdk.context.networkId === 84532 || this.sdk.context.networkId === 8453) {
            const { abi, address } = this.sdk.context.defaultContractConfigs.USDC;
            const usdcBal = await this.sdk.context.client.readContract({
                abi,
                address,
                functionName: 'balanceOf',
                args: [walletAddress],
            });
            return {
                USDC: {
                    balance: wei(usdcBal ?? 0, this.usdcDecimals),
                    token: this.tokensMap['USDC'],
                },
            };
        }
        if (!this.sdk.context.isMainnet)
            return {};
        const filteredTokens = [];
        const symbols = [];
        const tokensMap = {};
        this.tokenList.forEach((token) => {
            if (!FILTERED_TOKENS.includes(token.address.toLowerCase())) {
                symbols.push(token.symbol);
                tokensMap[token.symbol] = token;
                filteredTokens.push(token);
            }
        });
        const calls = [];
        for (const { address, symbol } of filteredTokens) {
            if (symbol !== CRYPTO_CURRENCY_MAP.ETH) {
                calls.push({
                    abi: erc20Abi,
                    address: address,
                    functionName: 'balanceOf',
                    args: [walletAddress],
                });
            }
        }
        const data = (await this.sdk.context.client.multicall({ contracts: calls }));
        const tokenBalances = {};
        data.forEach((value, i) => {
            if (value.result < 0n)
                return;
            const token = tokensMap[symbols[i]];
            tokenBalances[symbols[i]] = {
                balance: wei(value.result.toString(), token.decimals ?? 18),
                token,
            };
        });
        return tokenBalances;
    }
    // TODO: Move to token service
    async getTokenAllowances(currencyKeys, spenders) {
        const tokenAddresses = currencyKeys.reduce((acc, currencyKey) => {
            const tokenAddress = ADDRESSES[currencyKey][this.sdk.context.networkId] ??
                this.getTokenAddress(currencyKey, true);
            if (tokenAddress) {
                acc.push({
                    key: currencyKey,
                    address: tokenAddress,
                });
            }
            return acc;
        }, []);
        const callItems = spenders.reduce((acc, spender) => {
            const items = Object.values(tokenAddresses).map((tokenAddress) => ({
                spender: spender,
                ...tokenAddress,
            }));
            return acc.concat(items);
        }, []);
        const results = await this.sdk.context.client.multicall({
            contracts: callItems.map((item) => ({
                abi: erc20Abi,
                address: item.address,
                functionName: 'allowance',
                args: [this.sdk.context.walletAddress, item.spender],
            })),
            allowFailure: false,
        });
        return callItems.reduce((acc, item, i) => {
            acc[item.key] = {
                ...acc[item.key],
                [item.spender]: wei(results[i], this.getTokenDecimals(item.key)),
            };
            return acc;
        }, {});
    }
    checkIsAtomic(fromCurrencyKey, toCurrencyKey) {
        if (this.sdk.context.isL2 || !toCurrencyKey || !fromCurrencyKey) {
            return false;
        }
        return [toCurrencyKey, fromCurrencyKey].every((currency) => ATOMIC_EXCHANGES_L1.includes(currency));
    }
    getTokenDecimals(currencyKey) {
        return get(this.allTokensMap, [currencyKey, 'decimals'], undefined);
    }
    getCurrencyContract(currencyKey) {
        if (this.allTokensMap[currencyKey]) {
            const tokenAddress = this.getTokenAddress(currencyKey, true);
            return {
                address: tokenAddress,
                abi: erc20Abi,
            };
        }
        return null;
    }
    get oneInchApiUrl() {
        return `${process.env.NEXT_PUBLIC_SERVICES_PROXY}/1inch/swap/v5.2/${this.sdk.context.isL2 ? 10 : 1}/`;
    }
    getOneInchQuoteSwapParams(fromTokenAddress, toTokenAddress, amount, decimals) {
        return {
            fromTokenAddress,
            toTokenAddress,
            amount: wei(amount, decimals).toString(0, true),
        };
    }
    async getOneInchSwapParams(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals) {
        const params = this.getOneInchQuoteSwapParams(fromTokenAddress, toTokenAddress, amount, fromTokenDecimals);
        const res = await axios.get(this.oneInchApiUrl + 'swap', {
            params: {
                fromTokenAddress: params.fromTokenAddress,
                toTokenAddress: params.toTokenAddress,
                amount: params.amount,
                fromAddress: this.sdk.context.walletAddress,
                slippage: DEFAULT_1INCH_SLIPPAGE,
                PROTOCOLS,
                referrerAddress: KWENTA_ONE_INCH_REFERRAL_ADDRESS,
                disableEstimate: true,
                includeTokenInfo: true,
            },
        });
        return res.data;
    }
    async quoteOneInch(fromTokenAddress, toTokenAddress, amount, decimals) {
        const params = this.getOneInchQuoteSwapParams(fromTokenAddress, toTokenAddress, amount, decimals);
        const response = await axios.get(this.oneInchApiUrl + 'quote', {
            params: {
                fromTokenAddress: params.fromTokenAddress,
                toTokenAddress: params.toTokenAddress,
                amount: params.amount,
                PROTOCOLS,
                includeTokensInfo: true,
            },
        });
        return formatUnits(BigInt(response.data.toAmount), response.data.toToken.decimals);
    }
    async getPairRates(fromCurrencyKey, toCurrencyKey) {
        return this.checkIsAtomic(fromCurrencyKey, toCurrencyKey)
            ? await Promise.all([
                this.getAtomicRates(fromCurrencyKey),
                this.getAtomicRates(toCurrencyKey),
            ])
            : this.getExchangeRatesTupleForCurrencies(this.sdk.prices.currentPrices.onChain, fromCurrencyKey, toCurrencyKey);
    }
    async getOneInchApproveAddress() {
        const response = await axios.get(this.oneInchApiUrl + 'approve/spender');
        return response.data.address;
    }
    isCurrencyETH(currencyKey) {
        return currencyKey === CRYPTO_CURRENCY_MAP.ETH;
    }
    getTokenAddress(currencyKey, coingecko) {
        if (currencyKey != null) {
            if (this.isCurrencyETH(currencyKey)) {
                return coingecko ? ETH_COINGECKO_ADDRESS : ETH_ADDRESS;
            }
            else {
                return get(this.allTokensMap, [currencyKey, 'address'], null);
            }
        }
        else {
            return null;
        }
    }
    getCoingeckoPricesForCurrencies(coingeckoPrices, baseAddress) {
        if (!coingeckoPrices || !baseAddress) {
            return wei(0);
        }
        const base = (baseAddress === ETH_ADDRESS ? ETH_COINGECKO_ADDRESS : baseAddress).toLowerCase();
        if (!coingeckoPrices[base]) {
            return wei(0);
        }
        return wei(coingeckoPrices[base].usd);
    }
    getExchangeRatesForCurrencies(rates, base, quote) {
        base = ADDITIONAL_MARKETS.has(base) ? synthToAsset(base) : base;
        return !rates || !base || !quote || !rates[base] || !rates[quote]
            ? wei(0)
            : rates[base].div(rates[quote]);
    }
    getExchangeRatesTupleForCurrencies(rates, base, quote) {
        base = ADDITIONAL_MARKETS.has(base) ? synthToAsset(base) : base;
        const baseRate = !rates || !base || !rates[base] ? wei(0) : rates[base];
        const quoteRate = !rates || !quote || !rates[quote] ? wei(0) : rates[quote];
        return [baseRate, quoteRate];
    }
}
